import {
  createContext, Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState,
} from 'react';
import { ProcedureInterface } from '../types';
import UnmappedRecord from '../types/UnmappedRecord';
import {
  addNewTraitsRecord,
  mapTraitsRecord,
  dismissUnmappableRecord,
  useFetchPatientRecordSummaries,
  useFetchStudyProcedureConfig,
} from '../api/esourceService';
import PatientRecordSummary from '../types/PatientRecordSummary';
import UnmappedRecordStatus from '../enums/UnmappedRecordStatus';

interface Study {
  studyId: string;
  subjectId: string;
  protocolNumber: string;
}

interface TraitsReconciliationContextProps {
  studies?: Array<Study>;
  currentStudy?: Study;
  procedures?: Array<string>;
  currentProcedureId?: string;
  currentProcedureConfig?: ProcedureInterface;
  patientRecordSummaries?: Array<PatientRecordSummary>;
  setCurrentProcedureId: Dispatch<SetStateAction<string | undefined>>;
  setCurrentStudy: (study: Study) => void;
  loading?: boolean;
  addAsNewTraitsRecord: (unmappedRecordId: string, subjectId: string, traitsStudyProcedureKey: number) => Promise<void>;
  mapToTraitsRecord: (unmappedRecordId: string, subjectId: string, traitsStudyProcedureKey: number, patientRecordUid: string, isDeleted: boolean) => Promise<void>;
  dismissDeletedUnmappableRecord: (unmappedRecordId: string) => Promise<void>;
  unmappedRecordsWithStatus?: Array<UnmappedRecord>;
  hideResolvedCard: (unmappedRecordId: string) => void;
  /**
   * Error message to display, if any
   */
  errorMessage?: string;
  /**
   * Clear the error message
   */
  resetErrorMessage: () => void;
}

const TraitsReconciliationContext = createContext<TraitsReconciliationContextProps | Record<string, never>>({});

export default TraitsReconciliationContext;
export interface TraitsReconciliationContextProviderProps {
  children: ReactNode,
  siteId?: string,
  patientId?: string,
  unmappedRecords: Array<UnmappedRecord>,
}

export function TraitsReconciliationContextProvider(props: Readonly<TraitsReconciliationContextProviderProps>) {
  const {
    siteId, patientId, children, unmappedRecords = [],
  } = props;
  const [procedures, setProcedures] = useState<Array<string>>([]);
  const [currentStudy, setCurrentStudy] = useState<Study>();
  const [currentProcedureId, setCurrentProcedureId] = useState<string>();
  const traitsStudyId = unmappedRecords[0]?.traitsStudyId;
  const [unmappedRecordsWithStatus, setUnmappedRecordsWithStatus] = useState<Array<UnmappedRecord>>([]);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // isLoading first time, isFetching for refetches
  const { patientRecordSummaries, isFetching: isFetchingPatientRecordSummaries, isLoading: isLoadingPatientRecordSummaries } = useFetchPatientRecordSummaries(siteId, patientId, currentProcedureId, currentStudy?.subjectId);
  const { procedureConfig: currentProcedureConfig, isFetching: isFetchingProcedureConfig, isLoading: isLoadingProcedureConfig } = useFetchStudyProcedureConfig(traitsStudyId, currentProcedureId);
  const loading = isFetchingPatientRecordSummaries || isFetchingProcedureConfig || isLoadingPatientRecordSummaries || isLoadingProcedureConfig;
  const [patientRecordSummariesWithStatus, setPatientRecordSummariesWithStatus] = useState<Array<PatientRecordSummary>>([]);

  const studies = useMemo(() => {
    if (!unmappedRecords || unmappedRecords.length === 0) {
      return [];
    }
    return unmappedRecords.reduce((acc: Study[], record: UnmappedRecord) => {
      const firstDataPoint = record.dataPoints[0];
      if (!acc.some((s) => s.studyId === firstDataPoint?.study_id)) {
        acc.push({
          studyId: firstDataPoint?.study_id,
          protocolNumber: firstDataPoint?.protocol_number || 'N/A',
          subjectId: record.subjectId,
        });
      }
      return acc;
    }, []);
  }, [unmappedRecords]);

  useEffect(() => {
    setUnmappedRecordsWithStatus((prev) => {
      const newRecords = unmappedRecords.map((record) => new UnmappedRecord(record));
      const prevSet = new Set(prev?.map((records) => records.recordId));
      const newSet = new Set(newRecords.map((records) => records.recordId));
      if (newSet.size === prevSet.size && [...prevSet].every((value) => newSet.has(value))) {
        return prev;
      }
      return newRecords;
    });
  }, [unmappedRecords]);

  useEffect(() => {
    setCurrentStudy(studies ? studies[0] : undefined);
  }, [studies]);

  useEffect(() => {
    const currentStudyId = currentStudy?.studyId;
    if (currentStudyId) {
      const proceduresSet = new Set<string>();
      unmappedRecords.filter((record) => (record.eSourceStudyId === currentStudyId))
        .forEach((record) => {
          proceduresSet.add(record.traitsStudyProcedureId);
        });
      setProcedures(Array.from(proceduresSet));
    }
  }, [currentStudy, unmappedRecords]);// Do not depend on unmappedRecordsWithStatus here to avoid unnecessary re-renders

  useEffect(() => {
    // Only remove procedures if there are multiple procedures.
    // For the last procedure, user sees resolved page with procedure name after resolving all cards.
    if (procedures.length > 1) {
      const allRecordsResolved = unmappedRecordsWithStatus?.filter(
        (record) => record.eSourceStudyId === currentStudy?.studyId && record.traitsStudyProcedureId === currentProcedureId,
      ).every((record) => record.status === UnmappedRecordStatus.RESOLVED_HIDDEN) ?? false;
      if (allRecordsResolved) {
        setProcedures((currentProcedures) => currentProcedures?.filter((id) => id !== currentProcedureId));
      }
    }
  }, [unmappedRecordsWithStatus]);

  useEffect(() => {
    const allUnmappedRecordsResolved = unmappedRecordsWithStatus?.length > 0
        && unmappedRecordsWithStatus.every(
          (record) => record.status !== UnmappedRecordStatus.UNMAPPED,
        );

    if (allUnmappedRecordsResolved) {
      window.parent.postMessage({ key: 'dismissReconciliationBanner' }, '*');
    }
  }, [unmappedRecordsWithStatus]);

  useEffect(() => {
    setCurrentProcedureId(procedures ? procedures[0] : undefined);
  }, [procedures]);

  useEffect(() => {
    if (patientRecordSummaries) {
      const summariesWithStatus = patientRecordSummaries.map((summary) => ({ ...summary, inUse: summary.inUse ?? false }));
      setPatientRecordSummariesWithStatus(summariesWithStatus);
    }
  }, [patientRecordSummaries]);

  const updateUnmappedRecordsWithStatus = (unmappedRecordId: string, newStatus: UnmappedRecordStatus) => {
    setUnmappedRecordsWithStatus((currentUnmappedRecords) => currentUnmappedRecords?.map(
      (record) => (record.recordId === unmappedRecordId ? ({ ...record, status: newStatus }) : record),
    ));
  };

  const addAsNewTraitsRecord = useCallback(async (unmappedRecordId: string, subjectId: string, traitsStudyProcedureKey: number) => {
    try {
      setErrorMessage(undefined);
      const addResponse = await addNewTraitsRecord(siteId!, patientId!, unmappedRecordId, subjectId);
      if (addResponse) {
        updateUnmappedRecordsWithStatus(unmappedRecordId, UnmappedRecordStatus.ADDED);
        window.parent.postMessage({ key: 'refresh', procedure: traitsStudyProcedureKey }, '*');
      }
    } catch (error) {
      console.error('Error adding new traits record:', error);
      setErrorMessage('Failed to add new traits record. ');
    }
  }, [patientId, siteId]);

  const mapToTraitsRecord = useCallback(async (unmappedRecordId: string, subjectId: string, traitsStudyProcedureKey: number, patientRecordUid: string, isDeleted: boolean) => {
    try {
      setErrorMessage(undefined);
      const mapResponse = await mapTraitsRecord(siteId!, patientId!, unmappedRecordId, subjectId, patientRecordUid);
      if (!mapResponse) { return; }

      const newRecordStatus = isDeleted ? UnmappedRecordStatus.DELETED : UnmappedRecordStatus.MAPPED;
      updateUnmappedRecordsWithStatus(unmappedRecordId, newRecordStatus);
      setPatientRecordSummariesWithStatus((currentSummaries) => currentSummaries?.map(
        (summary) => (summary.multiRecordUid === patientRecordUid
          ? { ...summary, inUse: true }
          : summary),
      ));
      window.parent.postMessage({ key: 'refresh', procedure: traitsStudyProcedureKey }, '*');
    } catch (error) {
      console.error('Error mapping traits record:', error);
      setErrorMessage('Failed to map traits record. ');
    }
  }, [patientId, siteId]);

  const dismissDeletedUnmappableRecord = useCallback(async (unmappedRecordId: string) => {
    try {
      setErrorMessage(undefined);
      const dismissResponse = await dismissUnmappableRecord(siteId!, patientId!, unmappedRecordId);
      if (!dismissResponse) { return; }
      updateUnmappedRecordsWithStatus(unmappedRecordId, UnmappedRecordStatus.RESOLVED_HIDDEN);
    } catch (error) {
      console.error('Error dismissing unmappable record: ', error);
      setErrorMessage('Failed to dismiss unmappable record.');
    }
  }, [patientId, siteId]);

  const hideResolvedCard = (unmappedRecordId: string) => {
    updateUnmappedRecordsWithStatus(unmappedRecordId, UnmappedRecordStatus.RESOLVED_HIDDEN);
  };

  const resetErrorMessage = () => {
    setErrorMessage(undefined);
  };

  const value = useMemo(
    () => ({
      loading,
      studies,
      currentStudy,
      procedures,
      currentProcedureId,
      setCurrentProcedureId,
      setCurrentStudy,
      currentProcedureConfig,
      patientRecordSummaries: patientRecordSummariesWithStatus,
      addAsNewTraitsRecord,
      mapToTraitsRecord,
      dismissDeletedUnmappableRecord,
      unmappedRecordsWithStatus,
      hideResolvedCard,
      errorMessage,
      resetErrorMessage,
    }),
    [loading,
      studies,
      currentStudy,
      procedures,
      currentProcedureId,
      currentProcedureConfig,
      patientRecordSummariesWithStatus,
      unmappedRecordsWithStatus,
      addAsNewTraitsRecord,
      mapToTraitsRecord,
      dismissDeletedUnmappableRecord,
      errorMessage,
    ],
  );

  return (
    <TraitsReconciliationContext.Provider value={value}>
      {children}
    </TraitsReconciliationContext.Provider>
  );
}
